class DetailsModal extends withAnimation(HTMLElement) {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.modalContentWrapper = this.detailsContainer.querySelector('.modal__content-wrapper');
    this.modalContent = this.detailsContainer.querySelector('.modal__content') || this.detailsContainer;
    this.backDrop = this.querySelector('.modal__backdrop');
    this.initAnimation({ duration: parseFloat(this.getAttribute('data-duration')) || 0.25 });
    this.scrollLockEnable = true;
    this.focusableElements = [];
    this._pointerDownInside = false;
    this.init();
    DrawerManager.register(this, { side: 'bottom' });
    this.originalParent = null;
  }

  appendToBody() {
    if (this.hasAttribute('data-append-body') && this.modalContentWrapper.parentElement !== document.body) {
      this.originalParent = this.modalContentWrapper.parentElement;
      document.body.append(this.modalContentWrapper);
    }
  }

  restoreToParent() {
    if (this.originalParent && this.modalContentWrapper.parentElement === document.body) {
      this.originalParent.appendChild(this.modalContentWrapper);
      this.originalParent = null;
    }
  }

  init() {
    this.detailsContainer.addEventListener('keyup', e => this.onContainerKeyUp(e));
    this.detailsContainer.addEventListener('keydown', e => this.onContainerKeyDown(e));
    this.detailsContainer.addEventListener('focusout', e => this.onFocusOut(e));
    this.summaryToggle.addEventListener('click', e => this.onSummaryClick(e));
    this.querySelector('button[type="button"]')?.addEventListener('click', () => this.close());
    this.backDrop?.addEventListener('click', () => this.close());
    this.summaryToggle.setAttribute('role', 'button');
    this.detailsContainer.addEventListener('pointerdown', e => this.onContainerPointerDown(e));
  }

  isOpen = () => this.detailsContainer.hasAttribute('open');

  getFocusableElements() {
    const focusableSelector = 'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const elements = [];
    const shadowHosts = this.detailsContainer.querySelectorAll('*');
    shadowHosts.forEach(host => {
      if (host.shadowRoot) {
        const shadowElements = Array.from(host.shadowRoot.querySelectorAll(focusableSelector));
        elements.push(...shadowElements);
      }
    });
    const regularElements = Array.from(this.detailsContainer.querySelectorAll(focusableSelector));
    elements.push(...regularElements);
    return elements.filter(el => {
      const style = window.getComputedStyle(el);
      return !el.hidden && !el.disabled && style.display !== 'none' && style.visibility !== 'hidden' && el.tabIndex !== -1;
    });
  }

  updateFocusableElements() {
    this.focusableElements = this.getFocusableElements();
    return this.focusableElements;
  }

  onContainerKeyDown(event) {
    if (!this.isOpen()) return;
    const items = this.getFocusableElements();
    const index = items.indexOf(document.activeElement);
    const { code } = event;
    if (['ArrowUp', 'ArrowDown'].includes(code)) {
      event.preventDefault();
      const nextIndex = code === 'ArrowUp'
        ? (index > 0 ? index - 1 : items.length - 1)
        : (index < items.length - 1 ? index + 1 : 0);
      items[nextIndex]?.focus();
    }
  }

  onContainerKeyUp(event) {
    if (this.isOpen() && event.code === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }
  }

  onContainerPointerDown(event) {
    this._pointerDownInside = true;
  }

  onFocusOut(event) {
    if (!this.isOpen()) return;

    if (this._pointerDownInside) {
      this._pointerDownInside = false;
      return;
    }

    if (!this.contains(event.relatedTarget)) {
      setTimeout(() => this.isOpen() && this.close(false), 0);
    }
  }

  onSummaryClick(event) {
    event.preventDefault();
    if (this.isAnimating) return;
    const isOpen = event.target.closest('details')?.hasAttribute('open');
    isOpen ? this.close() : this.open(event);
  }

  onBodyClick = event => {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) {
      this.close(false);
    }
  }

  open(event) {
    if (this.isAnimating) return;

    const isMobile = DeviceUtils.isMobile;
    if (isMobile) this.appendToBody();

    const megaMenu = document.querySelector('mega-menu');
    if (megaMenu?.classList.contains('mega-menu--block')) {
      megaMenu.classList.remove('mega-menu--block');
      document.body.removeAttribute('data-mega-menu-block');
      megaMenu.forceClose?.();
    }

    this.onBodyClickEvent ||= this.onBodyClick.bind(this);
    event.target.closest('details')?.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);

    if (isMobile) {
      this.scrollLockEnable && scrollLock.lock();
      DrawerManager.setActiveWithSecondary(this);
      DrawerManager.diffrentSide() && DrawerManager.clearSecondaryIfOpen(this);
      document.body.classList.add('overflow-hidden');
    }
    requestAnimationFrame(() => {
      this.animateShowContent(isMobile);
    });
    setTimeout(() => {
      let focusableElement = this.detailsContainer.querySelector('[tabindex="-1"]');
      if (!focusableElement) {
        focusableElement = this.detailsContainer.querySelector('button, a[href], [tabindex]:not([tabindex="-1"])');
      } else if (focusableElement) {
        focusableElement.focus();
        trapFocus(this.detailsContainer, null);
      }
    }, 100);
  }

  close(focusToggle = true) {
    if (!this.isOpen() || this.isAnimating) return;
    this.animateHideContent(focusToggle);
  }

  hideContent(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    Object.assign(this.modalContent.style, {
      height: '', opacity: '', overflow: ''
    });
    this.isAnimating = false;
    const isMobile = DeviceUtils.isMobile;
    if (isMobile) {
      this.restoreToParent();
    }
    if (focusToggle) setTimeout(() => this.summaryToggle.focus(), 0);
  }

  forceClose() {
    if (!this.isOpen()) return;
    this.isAnimating = false;
    this.hideContent(false);
  }

  onShowActions(isMobile) {
    isMobile && DrawerManager.clearSecondaryIfOpen(this);
  }

  onCloseActions(focusToggle, isMobile) {
    this.hideContent(focusToggle);
    if (isMobile) {
      DrawerManager.unsetActive(this);
      document.body.classList.remove('overflow-hidden');
    }
  }

  async animateShowContent(isMobile) {
    await this.animateShow(this.modalContent, {
      heightAnimation: true,
      staggerSelector: isMobile ? false : '[data-stagger]',
      backDrop: isMobile ? this.backDrop : false,
      onComplete: () => this.onShowActions(isMobile)
    });
  }

  async animateHideContent(focusToggle = true) {
    const isMobile = DeviceUtils.isMobile;
    await this.animateHide(this.modalContent, {
      heightAnimation: isMobile,
      backDrop: isMobile ? this.backDrop : false,
      onComplete: () => this.onCloseActions(focusToggle, isMobile)
    });
  }
}

customElements.define('details-modal', DetailsModal);